import { recommendationDirection, type MetricPoint, type Recommendation } from '@lizrd/contracts';

/**
 * Terminal recommendation states — the opportunity is realized (validated) or
 * discarded (rejected/dismissed), plus superseded so a replaced recommendation
 * isn't double-counted against its replacement.
 */
export const TERMINAL_STATUSES = new Set(['validated', 'rejected', 'dismissed', 'superseded']);

/**
 * A recommendation that still represents recoverable saving: non-terminal (open,
 * accepted, or in validation), cost-reducing, with a positive estimate. The single
 * source of truth for "recoverable" across the headline boxes and the by-category
 * breakdown, so they always reconcile.
 */
export function isRecoverable(r: Recommendation): boolean {
  return (
    !TERMINAL_STATUSES.has(r.status) &&
    recommendationDirection(r.category) === 'reduce' &&
    r.estimatedMonthlySaving > 0
  );
}

/** Sum of estimated monthly saving over recoverable recommendations. */
export function recoverableSaving(recs: Recommendation[]): number {
  return recs.reduce((s, r) => (isRecoverable(r) ? s + r.estimatedMonthlySaving : s), 0);
}

/** How many recommendations are still recoverable. */
export function recoverableCount(recs: Recommendation[]): number {
  return recs.reduce((n, r) => (isRecoverable(r) ? n + 1 : n), 0);
}

/** Local-time YYYY-MM-DD (avoids the UTC drift of toISOString for day keys). */
export function localISODate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Monday 00:00 of the week containing `d`. */
export function weekStart(d: Date): Date {
  const w = new Date(d);
  w.setHours(0, 0, 0, 0);
  w.setDate(w.getDate() - ((w.getDay() + 6) % 7));
  return w;
}

/** First day 00:00 of the month containing `d`. */
export function monthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** The last `n` week-start dates (oldest→newest), including the current week. */
export function lastNWeekStarts(now: Date, n: number): Date[] {
  const cur = weekStart(now);
  const out: Date[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const w = new Date(cur);
    w.setDate(cur.getDate() - i * 7);
    out.push(w);
  }
  return out;
}

/** Only validated recommendations carry a realized saving. */
export function validatedRecs(recs: Recommendation[]): Recommendation[] {
  return recs.filter((r) => r.status === 'validated' && r.validatedAt);
}

/**
 * Bucket newly-realized monthly savings into the given week starts by validation
 * date — "how much proven saving landed each week." Returns one MetricPoint per
 * week (day = the week's Monday), aligned to `weeks` so multiple series share an
 * axis in {@link LineChart}.
 */
export function realizedByWeek(recs: Recommendation[], weeks: Date[]): MetricPoint[] {
  const totals = new Array(weeks.length).fill(0);
  for (const r of validatedRecs(recs)) {
    const t = new Date(r.validatedAt!).getTime();
    for (let i = weeks.length - 1; i >= 0; i--) {
      if (t >= weeks[i]!.getTime()) {
        totals[i] += r.realizedMonthlySaving ?? 0;
        break;
      }
    }
  }
  return weeks.map((w, i) => ({ day: localISODate(w), value: totals[i]! }));
}

/** Sum of realized monthly saving from recs validated on/after `since`. */
export function realizedSince(recs: Recommendation[], since: Date): number {
  return validatedRecs(recs)
    .filter((r) => new Date(r.validatedAt!) >= since)
    .reduce((s, r) => s + (r.realizedMonthlySaving ?? 0), 0);
}

/** Sum of realized monthly saving validated within [start, end). */
export function realizedBetween(recs: Recommendation[], start: Date, end: Date): number {
  return validatedRecs(recs)
    .filter((r) => {
      const t = new Date(r.validatedAt!);
      return t >= start && t < end;
    })
    .reduce((s, r) => s + (r.realizedMonthlySaving ?? 0), 0);
}

/** A date `days` ago at 00:00, or null for "all time". */
export function periodSince(days: number | null): Date | null {
  if (days == null) return null;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d;
}

export interface Funnel {
  produced: number;
  accepted: number;
  executed: number;
  validated: number;
}

/**
 * Conversion funnel over the recommendations *produced* in the window (by
 * `firstSeenAt`): of those, how many progressed to accepted, executed, and
 * validated. A recommendation counts as accepted once it's accepted or further
 * along; executed once it has an execution date; validated once measured.
 */
export function funnelCounts(recs: Recommendation[], since: Date | null): Funnel {
  const f: Funnel = { produced: 0, accepted: 0, executed: 0, validated: 0 };
  for (const r of recs) {
    if (since && new Date(r.firstSeenAt) < since) continue;
    f.produced++;
    const executed = r.executedAt != null;
    const accepted = executed || ['accepted', 'completed', 'validated'].includes(r.status);
    if (accepted) f.accepted++;
    if (executed) f.executed++;
    if (r.validatedAt != null) f.validated++;
  }
  return f;
}

/** Cumulative realized monthly saving over time (running total by validation
 * date), restricted to the window — for a "savings so far" growth line. */
export function cumulativeRealized(recs: Recommendation[], since: Date | null): MetricPoint[] {
  const inWindow = validatedRecs(recs).filter((r) => !since || new Date(r.validatedAt!) >= since);
  if (inWindow.length === 0) return [];

  // Total realized saving validated on each day.
  const byDay = new Map<string, number>();
  for (const r of inWindow) {
    const key = localISODate(new Date(r.validatedAt!));
    byDay.set(key, (byDay.get(key) ?? 0) + (r.realizedMonthlySaving ?? 0));
  }

  // Emit ONE point per day from the window start (or the first validation) through
  // today, carrying the running cumulative forward — so a day with no new validation
  // holds the line flat at yesterday's total rather than leaving a gap.
  const firstAt = new Date(Math.min(...inWindow.map((r) => new Date(r.validatedAt!).getTime())));
  const startAt = since ?? firstAt;
  const cursor = new Date(startAt.getFullYear(), startAt.getMonth(), startAt.getDate());
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const out: MetricPoint[] = [];
  let running = 0;
  for (; cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    const key = localISODate(cursor);
    running += byDay.get(key) ?? 0;
    out.push({ day: key, value: running });
  }
  return out;
}

export interface Saver {
  name: string;
  saving: number;
}

/** Rank people by realized saving from recs validated on/after `since`. */
export function topSaverPeople(recs: Recommendation[], since?: Date): Saver[] {
  const byName = new Map<string, number>();
  for (const r of validatedRecs(recs)) {
    if (since && new Date(r.validatedAt!) < since) continue;
    const name = r.executedByName ?? 'Unknown';
    byName.set(name, (byName.get(name) ?? 0) + (r.realizedMonthlySaving ?? 0));
  }
  return [...byName.entries()]
    .map(([name, saving]) => ({ name, saving }))
    .filter((s) => s.saving > 0)
    .sort((a, b) => b.saving - a.saving);
}
